import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import deepmerge from 'deepmerge';
import { Box, Chip, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import moment from 'moment';

import useNotification from '../../../../../../core/hooks/use_notification';
import { getDefaultPaginationByDateDescQueryValues } from '../../../../../../core/utils/api_utils';
import { getCustomerReservationsNotificationData, getHubsNotificationData, getPopsNotificationData } from '../../../../../../core/utils/notification_utils';
import PaginationControls from '../../../../../common/pagination_controls';
import { MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT } from '../../../../../../core/constants';
import { getCustomerReservationsAction, getCustomerReservationsState } from '../../../../../../features/reservation/get_customer_reservations';
import { getAllPopsAction, getAllPopsState } from '../../../../../../features/pop/get_all_pops';
import { ReservationStatusEnum } from '../../../../../../core/enums/reservation/reservation';
import Filters from './filters';
import { getReservationStatusColor } from '../../../../../common/utils/reservation_utils';
import ReservationDialog from './reservation_dialog';
import { getAllHubsAction } from '../../../../../../features/pop/get_all_hubs';
import ReservationOrderDialog from './reservation_order_dialog';
import ReturnReservationDialog from '../../../../common/return_reservation_dialog';

const initialQueryParams = {
    ...getDefaultPaginationByDateDescQueryValues,
    languageCode: 'PL',
    reservationStatuses: [ReservationStatusEnum.FINISHED.value],
};

const ReservationsList = ({ customerId }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    const [queryParams, setQueryParams] = useState({ ...initialQueryParams, customerId });

    const getCustomerReservations = useSelector(getCustomerReservationsState);
    const getAllPops = useSelector(getAllPopsState);

    const refreshList = useCallback(() => {
        dispatch(getCustomerReservationsAction(queryParams))
            .then(response => showErrorMessage(getCustomerReservationsNotificationData(response)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, queryParams]);

    useEffect(() => {
        refreshList();
    }, [refreshList]);

    useEffect(() => {
        dispatch(getAllHubsAction())
            .then(response => showErrorMessage(getHubsNotificationData(response)));
        dispatch(getAllPopsAction())
            .then(response => showErrorMessage(getPopsNotificationData(response)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    return (
        <Box>
            { getAllPops?.data && (
                <Box mb={ 4 }>
                    <Filters setQueryParams={ setQueryParams } queryParams={ queryParams }/>
                </Box>
            ) }

            { getCustomerReservations?.loading && (
                <Box mb={ 2 }>
                    <LinearProgress/>
                </Box>
            ) }

            { getCustomerReservations?.data?.content && (
                <Box>
                    <TableContainer component={ Paper } variant="outlined">
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Punkt odbioru</TableCell>
                                    <TableCell align="center">Data</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                    <TableCell align="center">Numer zamówienia</TableCell>
                                    <TableCell align="right" sx={ { minWidth: 160 } }></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                { getCustomerReservations?.data?.content?.map(reservation => (
                                    <TableRow key={ reservation?.reservationId } hover>
                                        <TableCell sx={ { fontWeight: 600 } }>
                                            { reservation?.reservationId }
                                        </TableCell>
                                        <TableCell>
                                            { getAllPops?.data?.map(pop => ({
                                                id: pop?.id,
                                                label: `${ pop?.hubName } / ${ pop?.name }`
                                            }))
                                                ?.find((pop) => pop?.id === reservation?.popId)?.label || '-' }
                                        </TableCell>
                                        <TableCell align="center">
                                            { moment(reservation?.date, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={ reservation?.displayStatusValue }
                                                color={ getReservationStatusColor(reservation?.status) }
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center" sx={ { fontFamily: 'monospace' } }>
                                            { reservation?.reservationOrderId }
                                        </TableCell>


                                        <TableCell align="right">
                                            <Stack direction="row" justifyContent="flex-end" alignItems="center"
                                                   spacing={ 1 }>
                                                <ReservationDialog reservation={ reservation }/>
                                                <ReservationOrderDialog reservation={ reservation }/>

                                                { [
                                                    ReservationStatusEnum.FINISHED.value,
                                                    ReservationStatusEnum.DELIVERED.value,
                                                    ReservationStatusEnum.PREPARED.value,
                                                    ReservationStatusEnum.PARTIALLY_RETURNED.value
                                                ].includes(reservation?.status) && (
                                                    <ReturnReservationDialog reservation={ reservation }
                                                                             refreshList={ refreshList }/>
                                                ) }
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                )) }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box mt={ 3 } display="flex" justifyContent="center">
                        <PaginationControls
                            currentPage={ getCustomerReservations?.data?.pageable?.pageNumber }
                            totalPages={ getCustomerReservations?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                </Box>
            ) }
        </Box>
    );
};

export default ReservationsList;